import React from 'react';
import { 
  Paper, 
  Typography, 
  Box, 
  Grid, 
  LinearProgress, 
  Chip, 
  Avatar, 
  Tooltip, 
  IconButton,
  useTheme 
} from '@mui/material';
import { Spa as EcoIcon, WaterDrop as WaterDropIcon, Co2 as Co2Icon, Landscape as LandscapeIcon, VerifiedUser as VerifiedIcon, InfoOutlined as InfoOutlinedIcon, Share as ShareIcon } from '@mui/icons-material';

const SustainabilityMetrics: React.FC = () => {
  const theme = useTheme();

  const metrics = [
    {
      label: 'Carbon Footprint',
      value: '12.4',
      unit: 'tons CO2e',
      target: '10.0',
      progress: 75,
      color: theme.palette.warning.main,
      icon: <Co2Icon />,
      trend: '-15% vs last year'
    },
    {
      label: 'Water Efficiency',
      value: '92',
      unit: '%',
      target: '95',
      progress: 92,
      color: theme.palette.info.main,
      icon: <WaterDropIcon />,
      trend: '+5% improvement'
    },
    {
      label: 'Soil Health Index',
      value: '8.5',
      unit: '/ 10',
      target: '9.0',
      progress: 85,
      color: theme.palette.success.main,
      icon: <LandscapeIcon />,
      trend: 'Regenerative'
    }
  ];

  return (
    <Paper 
      elevation={0} 
      sx={{ 
        p: 3, 
        borderRadius: 4, 
        border: `1px solid ${theme.palette.divider}`,
        background: `linear-gradient(135deg, ${theme.palette.background.paper} 0%, ${theme.palette.action.hover} 100%)`
      }}
    >
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
        <Box display="flex" alignItems="center" gap={1}>
          <EcoIcon color="success" fontSize="large" />
          <Box>
            <Typography variant="h6" fontWeight="bold">Sustainability Scorecard</Typography>
            <Box display="flex" alignItems="center" gap={0.5}>
              <VerifiedIcon sx={{ fontSize: 16, color: theme.palette.success.main }} />
              <Typography variant="caption" color="text.secondary">Blockchain Verified</Typography>
            </Box>
          </Box>
        </Box>
        <Tooltip title="Share Impact Report">
          <IconButton size="small">
            <ShareIcon fontSize="small" />
          </IconButton>
        </Tooltip>
      </Box>

      <Grid container spacing={2}>
        {metrics.map((metric, index) => (
          <Grid item xs={12} sm={4} key={index}>
            <Paper 
              elevation={0} 
              sx={{ 
                p: 2, 
                bgcolor: 'background.default', 
                borderRadius: 3,
                height: '100%',
                display: 'flex',
                flexDirection: 'column',
                justifyContent: 'space-between'
              }}
            >
              <Box display="flex" justifyContent="space-between" alignItems="start" mb={1}>
                <Avatar 
                  sx={{ 
                    bgcolor: `${metric.color}20`, 
                    color: metric.color,
                    width: 32,
                    height: 32
                  }}
                >
                  {metric.icon}
                </Avatar>
                <Chip 
                  label={metric.trend} 
                  size="small" 
                  sx={{ 
                    fontSize: '0.65rem', 
                    height: 20,
                    bgcolor: `${theme.palette.success.main}15`,
                    color: theme.palette.success.dark,
                    fontWeight: 'bold'
                  }} 
                />
              </Box>
              
              <Box>
                <Typography variant="body2" color="text.secondary">{metric.label}</Typography>
                <Box display="flex" alignItems="baseline" gap={0.5}>
                  <Typography variant="h5" fontWeight="bold">{metric.value}</Typography>
                  <Typography variant="caption" color="text.secondary">{metric.unit}</Typography>
                </Box>
              </Box>

              <Box mt={2}>
                <Box display="flex" justifyContent="space-between" mb={0.5}>
                  <Typography variant="caption" color="text.secondary">Target: {metric.target}</Typography>
                  <Typography variant="caption" fontWeight="bold">{metric.progress}%</Typography>
                </Box>
                <LinearProgress 
                  variant="determinate" 
                  value={metric.progress} 
                  sx={{ 
                    height: 6, 
                    borderRadius: 3,
                    bgcolor: `${metric.color}20`,
                    '& .MuiLinearProgress-bar': {
                      bgcolor: metric.color,
                      borderRadius: 3
                    }
                  }} 
                />
              </Box>
            </Paper>
          </Grid>
        ))}
      </Grid>

      <Box mt={3} p={2} sx={{ bgcolor: `${theme.palette.success.main}10`, borderRadius: 2 }}>
        <Typography variant="subtitle2" fontWeight="bold" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <InfoOutlinedIcon fontSize="small" color="success" />
          Impact Analysis
        </Typography>
        <Typography variant="body2" color="text.secondary">
          Your farm has sequestered <strong>2.4 tons</strong> of additional carbon this quarter through cover cropping. 
          Qualifies for <strong>$450</strong> in carbon credits.
        </Typography>
      </Box>
    </Paper>
  );
};

export default SustainabilityMetrics;
